import { useEffect, useState, useImperativeHandle, forwardRef } from "react";
import api from "../lib/api";
import { Eye, MousePointerClick, Share2, RefreshCw, ExternalLink } from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";

/**
 * Workspace dashboard tile — "Your shared drafts performance".
 * Pulls /api/share/mine/stats which only ever joins on the user's OWN opaque share_ids.
 */
const ShareAnalyticsTile = forwardRef((_, ref) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/share/mine/stats");
      setStats(data);
    } catch (e) {
      // silent fail — non-blocking widget
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);
  useImperativeHandle(ref, () => ({ refresh: load }));

  if (!stats) return null;

  const t = stats.totals;
  const ctrPct = (t.ctr * 100).toFixed(1);

  return (
    <div className="sr-card p-4 mt-4" data-testid="share-analytics-tile">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <Share2 className="h-3.5 w-3.5 text-teal" />
          <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-white/50">Your shared drafts performance</span>
        </div>
        <Button
          data-testid="share-analytics-refresh"
          size="sm"
          variant="ghost"
          onClick={load}
          className="h-7 px-2 text-white/40 hover:text-white"
        >
          <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} />
        </Button>
      </div>

      <div className="grid grid-cols-4 gap-3 mb-3">
        <Stat label="Shares" value={t.shares} />
        <Stat label="Reads" value={t.views} icon={Eye} accent />
        <Stat label="Sign-up clicks" value={t.clicks} icon={MousePointerClick} accent />
        <Stat label="CTR" value={`${ctrPct}%`} />
      </div>

      {stats.shares.length > 0 ? (
        <div className="border-t border-border pt-3 space-y-1.5 max-h-[200px] sr-scroll overflow-y-auto" data-testid="share-analytics-list">
          {stats.shares.slice(0, 10).map((s) => (
            <a
              key={s.share_id}
              href={`/shared/${s.share_id}`}
              target="_blank"
              rel="noreferrer noopener"
              className="flex items-center justify-between gap-3 py-1.5 px-2 rounded-sm hover:bg-white/[0.03] group"
              data-testid={`share-row-${s.share_id}`}
            >
              <div className="min-w-0 flex-1">
                <div className="text-[12px] text-white/80 truncate">{s.preview || "(empty)"}</div>
                <div className="text-[10px] font-mono text-white/35 mt-0.5">
                  {new Date(s.created_at).toLocaleString()} · {s.share_id.slice(0, 10)}…
                </div>
              </div>
              <div className="flex items-center gap-3 text-[11px] font-mono shrink-0">
                <span className="text-white/60 inline-flex items-center gap-1"><Eye className="h-3 w-3" />{s.view_count}</span>
                <span className="text-teal inline-flex items-center gap-1"><MousePointerClick className="h-3 w-3" />{s.click_count}</span>
                <ExternalLink className="h-3 w-3 text-white/30 group-hover:text-teal" />
              </div>
            </a>
          ))}
        </div>
      ) : (
        <div className="text-[12px] text-white/40 border-t border-border pt-3">
          No public shares yet. Click <span className="text-teal">Share</span> after generating to drop a viral CTA into your drafts.
        </div>
      )}
    </div>
  );
});

ShareAnalyticsTile.displayName = "ShareAnalyticsTile";

function Stat({ label, value, icon: Icon, accent }) {
  return (
    <div className="bg-black/30 border border-border p-2.5 rounded-sm">
      <div className="text-[9px] font-mono uppercase tracking-[0.16em] text-white/40 flex items-center gap-1">
        {Icon && <Icon className="h-2.5 w-2.5" />} {label}
      </div>
      <div className={`mt-1 text-xl font-display font-black ${accent ? "text-teal" : "text-white"}`}>{value}</div>
    </div>
  );
}

export default ShareAnalyticsTile;
